import { RE2JS } from 're2js';

import { type ErrorBandsConfig, ErrorBandType } from './error-bands';

/**
 * Error bands rule for series matched by a chart's dynamic selection (attribute-pattern source).
 * Bound metric names are templates resolved per series, for example "{metric}_upper_ci".
 */
export type ErrorBandsTemplateConfig = {
  type: ErrorBandType;
  options: ErrorBandsTemplateOptions;
};

export type ErrorBandsTemplateOptions = {
  /**
   * Optional RE2 pattern (the dynamic selection syntax), case-sensitive unless it uses (?i).
   * Series that don't match keep default bands.
   */
  metricNamePattern?: string;
  upperBandMetricTemplate?: string;
  lowerBandMetricTemplate?: string;
  upperLowerBandMetricTemplate?: string;
};

export type ErrorBandsTemplateField = keyof ErrorBandsTemplateOptions;

type ErrorBandsTemplateBoundField = Exclude<ErrorBandsTemplateField, 'metricNamePattern'>;

export type ErrorBandsTemplateIssue = {
  field: ErrorBandsTemplateField | 'type';
  message: string;
};

export type ErrorBandsTemplateResolution =
  | { status: 'invalid-config' }
  | { status: 'not-matched' }
  | { status: 'empty-bound-name' }
  | { status: 'same-bound-metric' }
  | { status: 'resolved'; config: ErrorBandsConfig };

type ErrorBandsTemplateSettings = {
  customErrorBandsEnabled?: boolean;
  customErrorBandsConfigByMetricName?: Record<string, ErrorBandsConfig>;
  customErrorBandsConfigForAttributePattern?: ErrorBandsTemplateConfig;
};

const TEMPLATE_OPTION_FIELDS: readonly ErrorBandsTemplateField[] = [
  'metricNamePattern',
  'upperBandMetricTemplate',
  'lowerBandMetricTemplate',
  'upperLowerBandMetricTemplate',
];

const BOUND_FIELDS_BY_TYPE: Record<ErrorBandType, readonly ErrorBandsTemplateBoundField[]> = {
  [ErrorBandType.AUTO_MIN_MAX]: [],
  [ErrorBandType.MANUAL_SYMMETRICAL]: ['upperLowerBandMetricTemplate'],
  [ErrorBandType.MANUAL_TWO_SIDED]: ['upperBandMetricTemplate', 'lowerBandMetricTemplate'],
  [ErrorBandType.DISABLED]: [],
};

const FIELD_LABELS: Record<ErrorBandsTemplateField, string> = {
  metricNamePattern: 'Metric name pattern',
  upperBandMetricTemplate: 'Upper bound template',
  lowerBandMetricTemplate: 'Lower bound template',
  upperLowerBandMetricTemplate: 'Bounds template',
};

const FIELD_EXAMPLES: Record<ErrorBandsTemplateBoundField, string> = {
  upperBandMetricTemplate: '{metric}_upper_ci',
  lowerBandMetricTemplate: '{metric}_lower_ci',
  upperLowerBandMetricTemplate: '{metric}_stderr',
};

const TYPE_LABELS: Record<ErrorBandType, string> = {
  [ErrorBandType.AUTO_MIN_MAX]: 'default',
  [ErrorBandType.MANUAL_SYMMETRICAL]: 'symmetrical',
  [ErrorBandType.MANUAL_TWO_SIDED]: 'two-sided',
  [ErrorBandType.DISABLED]: 'disabled',
};

const ERROR_BAND_TYPES: ReadonlySet<string> = new Set<string>(Object.values(ErrorBandType));
const METRIC_PLACEHOLDER = 'metric';
const IDENTIFIER_REGEX = /^[A-Za-z_][A-Za-z0-9_]*$/;
const GROUP_NUMBER_REGEX = /^\d+$/;
const CACHE_LIMIT = 200;
const MATCH_CACHE_LIMIT = 20_000;

function isErrorBandType(value: unknown): value is ErrorBandType {
  return typeof value === 'string' && ERROR_BAND_TYPES.has(value);
}

/**
 * Reads a persisted or DSL value. Returns undefined for malformed values and drops unknown or
 * empty option fields, so runtime code never has to handle partial JSON.
 */
export function normalizeErrorBandsTemplateConfig(
  value: unknown,
): ErrorBandsTemplateConfig | undefined {
  if (!isRecord(value) || !isErrorBandType(value.type)) {
    return undefined;
  }

  const rawOptions = isRecord(value.options) ? value.options : {};
  const options: ErrorBandsTemplateOptions = {};

  for (const field of TEMPLATE_OPTION_FIELDS) {
    const fieldValue = rawOptions[field];

    if (typeof fieldValue === 'string' && fieldValue.length > 0) {
      options[field] = fieldValue;
    }
  }

  return { type: value.type, options };
}

/** Returns all issues of the config at once, in field order. */
export function validateErrorBandsTemplateConfig(
  config: ErrorBandsTemplateConfig,
): ErrorBandsTemplateIssue[] {
  return [...compileConfig(config).issues];
}

/**
 * Lists the placeholders a bound template can use with the given metric name pattern, or
 * undefined when the pattern is not a valid regular expression.
 */
export function getErrorBandsTemplatePlaceholders(
  metricNamePattern: string | undefined,
): string[] | undefined {
  const pattern = analyzePattern(metricNamePattern);

  return pattern.status === 'invalid' ? undefined : listPlaceholders(pattern);
}

export function resolveErrorBandsTemplateForMetric(
  config: ErrorBandsTemplateConfig,
  metricName: string,
): ErrorBandsTemplateResolution {
  return compileConfig(config).resolve(metricName);
}

/**
 * Returns concrete error bands for one series, or undefined when the series keeps its default
 * bands (invalid config, metric name pattern doesn't match, a bound name resolves to "", or the
 * upper and lower bounds resolve to the same metric).
 */
export function resolveErrorBandsTemplateConfig(
  config: ErrorBandsTemplateConfig,
  metricName: string,
): ErrorBandsConfig | undefined {
  const resolution = resolveErrorBandsTemplateForMetric(config, metricName);

  return resolution.status === 'resolved' ? resolution.config : undefined;
}

/**
 * Returns the given series that are also resolved bound metrics of other given series, that is,
 * bound metrics the dynamic selection plots as separate lines.
 */
export function getErrorBandsTemplatePlottedBoundMetricNames(
  config: ErrorBandsTemplateConfig,
  metricNames: readonly string[],
): string[] {
  return [...resolveSeries(config, metricNames).plottedBoundMetricNames];
}

/**
 * Adds template-resolved configs for the given metric names. Explicit per-metric configs win.
 * Series that are bound metrics of other resolved series are skipped, so bounds of bounds are
 * never requested. Returns the input record itself when nothing was added.
 */
export function expandErrorBandsTemplateConfig({
  templateConfig,
  configByMetricName,
  metricNames,
}: {
  templateConfig: ErrorBandsTemplateConfig | undefined;
  configByMetricName: Record<string, ErrorBandsConfig> | undefined;
  metricNames: readonly string[];
}): Record<string, ErrorBandsConfig> | undefined {
  if (!templateConfig || metricNames.length === 0) {
    return configByMetricName;
  }

  const candidateMetricNames = metricNames.filter(
    (metricName) => !configByMetricName || !hasOwn(configByMetricName, metricName),
  );
  const { resolvedConfigs, plottedBoundMetricNames } = resolveSeries(
    templateConfig,
    candidateMetricNames,
  );
  const expandedEntries = [...resolvedConfigs].filter(
    ([metricName]) => !plottedBoundMetricNames.has(metricName),
  );

  if (expandedEntries.length === 0) {
    return configByMetricName;
  }

  return { ...configByMetricName, ...Object.fromEntries(expandedEntries) };
}

/**
 * Render-time only: returns chart options whose customErrorBandsConfigByMetricName also contains
 * the template rule resolved for the given dynamically selected metric names. The input object is
 * never mutated and is returned as is when nothing changes. Never persist the result.
 */
export function applyErrorBandsTemplateToChartOptions<TOptions extends ErrorBandsTemplateSettings>(
  options: TOptions,
  dynamicMetricNames: readonly string[],
): TOptions {
  if (!options.customErrorBandsEnabled || !options.customErrorBandsConfigForAttributePattern) {
    return options;
  }

  const configByMetricName = expandErrorBandsTemplateConfig({
    templateConfig: options.customErrorBandsConfigForAttributePattern,
    configByMetricName: options.customErrorBandsConfigByMetricName,
    metricNames: dynamicMetricNames,
  });

  if (configByMetricName === options.customErrorBandsConfigByMetricName) {
    return options;
  }

  return { ...options, customErrorBandsConfigByMetricName: configByMetricName };
}

type ValidPattern = {
  status: 'valid';
  source: string;
  regex: RE2JS;
  groupCount: number;
  // Named groups ordered by group number.
  groupIndexByName: ReadonlyMap<string, number>;
};

type PatternAnalysis = { status: 'none' } | { status: 'invalid'; message: string } | ValidPattern;

type TemplateToken =
  | { kind: 'literal'; text: string }
  | { kind: 'metric' }
  | { kind: 'group-number'; groupNumber: number; placeholder: string }
  | { kind: 'group-name'; groupName: string; placeholder: string };

type ParsedTemplate = {
  tokens: TemplateToken[];
  unmatchedOpeningBracePositions: number[];
  unmatchedClosingBracePositions: number[];
  invalidPlaceholders: string[];
  usesGroupZero: boolean;
};

type CompiledConfig = {
  issues: ErrorBandsTemplateIssue[];
  resolve: (metricName: string) => ErrorBandsTemplateResolution;
};

// Capture group values of one match, indexed by group number ("" for groups that didn't take
// part in the match), or null when the pattern doesn't match.
type PatternMatch = readonly string[] | null;

const NO_PATTERN: PatternAnalysis = { status: 'none' };
const INVALID_CONFIG_RESOLUTION: ErrorBandsTemplateResolution = { status: 'invalid-config' };
const NOT_MATCHED_RESOLUTION: ErrorBandsTemplateResolution = { status: 'not-matched' };
const EMPTY_BOUND_NAME_RESOLUTION: ErrorBandsTemplateResolution = { status: 'empty-bound-name' };
const SAME_BOUND_METRIC_RESOLUTION: ErrorBandsTemplateResolution = { status: 'same-bound-metric' };

const patternCache = new Map<string, PatternAnalysis>();
const configCache = new Map<string, CompiledConfig>();
// RE2 matching is linear-time but much slower than native regexes, so matches are reused while
// only the templates change and across re-renders. Keyed by pattern, then by metric name.
const matchCache = new Map<string, Map<string, PatternMatch>>();
let matchCacheSize = 0;

function resolveSeries(
  config: ErrorBandsTemplateConfig,
  metricNames: readonly string[],
): { resolvedConfigs: Map<string, ErrorBandsConfig>; plottedBoundMetricNames: Set<string> } {
  const seriesNames = new Set(metricNames);
  const resolvedConfigs = new Map<string, ErrorBandsConfig>();
  const plottedBoundMetricNames = new Set<string>();

  for (const metricName of seriesNames) {
    const resolvedConfig = resolveErrorBandsTemplateConfig(config, metricName);

    if (!resolvedConfig) {
      continue;
    }

    resolvedConfigs.set(metricName, resolvedConfig);

    for (const boundMetricName of getBoundMetricNames(resolvedConfig)) {
      if (boundMetricName !== metricName && seriesNames.has(boundMetricName)) {
        plottedBoundMetricNames.add(boundMetricName);
      }
    }
  }

  return { resolvedConfigs, plottedBoundMetricNames };
}

function getBoundMetricNames({ options }: ErrorBandsConfig): string[] {
  return [options.upperBandMetric, options.lowerBandMetric, options.upperLowerBandMetric].flatMap(
    (boundMetric) => (boundMetric ? [boundMetric.name] : []),
  );
}

function compileConfig(config: ErrorBandsTemplateConfig): CompiledConfig {
  const { type, options } = config;
  const cacheKey = JSON.stringify([
    type,
    options.metricNamePattern,
    options.upperBandMetricTemplate,
    options.lowerBandMetricTemplate,
    options.upperLowerBandMetricTemplate,
  ]);
  const cached = configCache.get(cacheKey);

  if (cached) {
    return cached;
  }

  const compiled = createCompiledConfig(config);
  setCached(configCache, cacheKey, compiled);

  return compiled;
}

function createCompiledConfig({ type, options }: ErrorBandsTemplateConfig): CompiledConfig {
  const issues: ErrorBandsTemplateIssue[] = [];

  if (!isErrorBandType(type)) {
    return {
      issues: [
        {
          field: 'type',
          message: `Unknown error band type "${String(type)}". Use one of: ${formatQuotedList([...ERROR_BAND_TYPES])}.`,
        },
      ],
      resolve: () => INVALID_CONFIG_RESOLUTION,
    };
  }

  const { metricNamePattern } = options;
  const pattern = analyzePattern(metricNamePattern);

  if (metricNamePattern && hasSurroundingWhitespace(metricNamePattern)) {
    issues.push({
      field: 'metricNamePattern',
      message: describeSurroundingWhitespace(FIELD_LABELS.metricNamePattern, metricNamePattern),
    });
  }

  if (pattern.status === 'invalid') {
    issues.push({ field: 'metricNamePattern', message: pattern.message });
  }

  if (pattern.status === 'valid' && pattern.groupIndexByName.has(METRIC_PLACEHOLDER)) {
    issues.push({
      field: 'metricNamePattern',
      message: `${FIELD_LABELS.metricNamePattern} defines a capture group named "${METRIC_PLACEHOLDER}", but {${METRIC_PLACEHOLDER}} always means the full metric name. Rename the group, for example (?<base>...).`,
    });
  }

  const boundFields = BOUND_FIELDS_BY_TYPE[type];
  const templates = new Map<ErrorBandsTemplateBoundField, TemplateToken[]>();

  for (const field of boundFields) {
    const template = options[field];

    if (!template) {
      issues.push({
        field,
        message: `${FIELD_LABELS[field]} is required for ${TYPE_LABELS[type]} error bands. For example: ${FIELD_EXAMPLES[field]}`,
      });
      continue;
    }

    if (hasSurroundingWhitespace(template)) {
      issues.push({ field, message: describeSurroundingWhitespace(FIELD_LABELS[field], template) });
    }

    const parsed = parseTemplate(template);
    issues.push(
      ...describeTemplateProblems(FIELD_LABELS[field], parsed, pattern).map((message) => ({
        field,
        message,
      })),
    );
    templates.set(field, parsed.tokens);
  }

  const { upperBandMetricTemplate, lowerBandMetricTemplate } = options;

  if (
    type === ErrorBandType.MANUAL_TWO_SIDED &&
    upperBandMetricTemplate &&
    upperBandMetricTemplate === lowerBandMetricTemplate
  ) {
    issues.push({
      field: 'lowerBandMetricTemplate',
      message: `Upper and lower bound templates are identical ("${upperBandMetricTemplate}"). Use different templates, for example ${FIELD_EXAMPLES.upperBandMetricTemplate} and ${FIELD_EXAMPLES.lowerBandMetricTemplate}.`,
    });
  }

  if (issues.length > 0) {
    return { issues, resolve: () => INVALID_CONFIG_RESOLUTION };
  }

  return {
    issues,
    resolve: (metricName) => {
      let groups: readonly string[] | undefined;

      if (pattern.status === 'valid') {
        const match = matchPattern(pattern, metricName);

        if (!match) {
          return NOT_MATCHED_RESOLUTION;
        }

        groups = match;
      }

      const resolveBoundName = (field: ErrorBandsTemplateBoundField) =>
        renderTemplate(templates.get(field) ?? [], metricName, groups, pattern);

      switch (type) {
        case ErrorBandType.MANUAL_SYMMETRICAL: {
          const upperLowerBandMetricName = resolveBoundName('upperLowerBandMetricTemplate');

          if (!upperLowerBandMetricName) {
            return EMPTY_BOUND_NAME_RESOLUTION;
          }

          return {
            status: 'resolved',
            config: {
              type,
              options: { upperLowerBandMetric: floatSeries(upperLowerBandMetricName) },
            },
          };
        }

        case ErrorBandType.MANUAL_TWO_SIDED: {
          const upperBandMetricName = resolveBoundName('upperBandMetricTemplate');
          const lowerBandMetricName = resolveBoundName('lowerBandMetricTemplate');

          if (!upperBandMetricName || !lowerBandMetricName) {
            return EMPTY_BOUND_NAME_RESOLUTION;
          }

          if (upperBandMetricName === lowerBandMetricName) {
            return SAME_BOUND_METRIC_RESOLUTION;
          }

          return {
            status: 'resolved',
            config: {
              type,
              options: {
                upperBandMetric: floatSeries(upperBandMetricName),
                lowerBandMetric: floatSeries(lowerBandMetricName),
              },
            },
          };
        }

        default:
          return { status: 'resolved', config: { type, options: {} } };
      }
    },
  };
}

function analyzePattern(metricNamePattern: string | undefined): PatternAnalysis {
  if (!metricNamePattern) {
    return NO_PATTERN;
  }

  const cached = patternCache.get(metricNamePattern);

  if (cached) {
    return cached;
  }

  const analysis = compilePattern(metricNamePattern);
  setCached(patternCache, metricNamePattern, analysis);

  return analysis;
}

// RE2 keeps matching linear in the metric name length, so a pattern like ^(.+)+x$ can't freeze
// the page. This is also the engine behind dynamic selection.
function compilePattern(metricNamePattern: string): PatternAnalysis {
  let regex: RE2JS;

  try {
    regex = RE2JS.compile(metricNamePattern);
  } catch (error) {
    const engineMessage = error instanceof Error ? error.message : String(error);

    return {
      status: 'invalid',
      message: `${FIELD_LABELS.metricNamePattern} "${metricNamePattern}" is not a valid RE2 regular expression (the same syntax as dynamic selection): ${engineMessage}.`,
    };
  }

  return {
    status: 'valid',
    source: metricNamePattern,
    regex,
    groupCount: regex.groupCount(),
    groupIndexByName: readNamedGroups(regex),
  };
}

function readNamedGroups(regex: RE2JS): ReadonlyMap<string, number> {
  const namedGroups: unknown = regex.namedGroups();
  const entries: [string, number][] = [];

  if (isRecord(namedGroups)) {
    for (const [groupName, groupNumber] of Object.entries(namedGroups)) {
      if (typeof groupNumber === 'number') {
        entries.push([groupName, groupNumber]);
      }
    }
  }

  entries.sort(([, left], [, right]) => left - right);

  return new Map(entries);
}

function matchPattern(pattern: ValidPattern, metricName: string): PatternMatch {
  const cachedMatches = matchCache.get(pattern.source);
  const cachedMatch = cachedMatches?.get(metricName);

  if (cachedMatch !== undefined) {
    return cachedMatch;
  }

  const matcher = pattern.regex.matcher(metricName);
  let match: PatternMatch = null;

  // find() is unanchored, like a JavaScript RegExp exec without flags.
  if (matcher.find()) {
    const groups: string[] = [];

    for (let groupNumber = 0; groupNumber <= pattern.groupCount; groupNumber++) {
      const value: unknown = matcher.group(groupNumber);
      groups.push(typeof value === 'string' ? value : '');
    }

    match = groups;
  }

  if (matchCacheSize >= MATCH_CACHE_LIMIT) {
    matchCache.clear();
    matchCacheSize = 0;
  }

  const matches = matchCache.get(pattern.source) ?? new Map<string, PatternMatch>();
  matches.set(metricName, match);
  matchCache.set(pattern.source, matches);
  matchCacheSize += 1;

  return match;
}

function parseTemplate(template: string): ParsedTemplate {
  const parsed: ParsedTemplate = {
    tokens: [],
    unmatchedOpeningBracePositions: [],
    unmatchedClosingBracePositions: [],
    invalidPlaceholders: [],
    usesGroupZero: false,
  };
  let literal = '';
  let index = 0;

  const flushLiteral = () => {
    if (literal) {
      parsed.tokens.push({ kind: 'literal', text: literal });
      literal = '';
    }
  };

  while (index < template.length) {
    const char = template[index];
    const nextChar = template[index + 1];

    if ((char === '{' || char === '}') && nextChar === char) {
      literal += char;
      index += 2;
      continue;
    }

    if (char === '}') {
      parsed.unmatchedClosingBracePositions.push(index + 1);
      index += 1;
      continue;
    }

    if (char !== '{') {
      literal += char;
      index += 1;
      continue;
    }

    const closingIndex = template.indexOf('}', index + 1);

    if (closingIndex === -1) {
      parsed.unmatchedOpeningBracePositions.push(index + 1);
      break;
    }

    const name = template.slice(index + 1, closingIndex);
    const placeholder = template.slice(index, closingIndex + 1);
    flushLiteral();

    if (name === METRIC_PLACEHOLDER) {
      parsed.tokens.push({ kind: 'metric' });
    } else if (GROUP_NUMBER_REGEX.test(name)) {
      const groupNumber = Number(name);

      if (groupNumber === 0) {
        parsed.usesGroupZero = true;
      }

      parsed.tokens.push({ kind: 'group-number', groupNumber, placeholder });
    } else if (IDENTIFIER_REGEX.test(name)) {
      parsed.tokens.push({ kind: 'group-name', groupName: name, placeholder });
    } else {
      parsed.invalidPlaceholders.push(placeholder);
    }

    index = closingIndex + 1;
  }

  flushLiteral();

  return parsed;
}

function describeTemplateProblems(
  label: string,
  parsed: ParsedTemplate,
  pattern: PatternAnalysis,
): string[] {
  const problems: string[] = [];
  const {
    unmatchedOpeningBracePositions,
    unmatchedClosingBracePositions,
    invalidPlaceholders,
    usesGroupZero,
  } = parsed;

  if (unmatchedOpeningBracePositions.length > 0) {
    problems.push(
      `${label} has an unmatched "{" at ${formatPositions(unmatchedOpeningBracePositions)}. Close the placeholder with "}" or write "{{" for a literal brace.`,
    );
  }

  if (unmatchedClosingBracePositions.length > 0) {
    problems.push(
      `${label} has an unmatched "}" at ${formatPositions(unmatchedClosingBracePositions)}. Write "}}" for a literal brace.`,
    );
  }

  if (invalidPlaceholders.length > 0) {
    problems.push(
      `${label} has ${pluralize(invalidPlaceholders.length, 'an invalid placeholder', 'invalid placeholders')} ${formatQuotedList(invalidPlaceholders)}. Use {metric}, {1} or {name}, and write "{{" or "}}" for literal braces.`,
    );
  }

  if (usesGroupZero) {
    problems.push(
      `${label} uses "{0}", but capture groups are numbered from {1}. Use {metric} for the full metric name.`,
    );
  }

  const groupPlaceholders = unique(
    parsed.tokens.flatMap((token) =>
      (token.kind === 'group-number' && token.groupNumber > 0) || token.kind === 'group-name'
        ? [token.placeholder]
        : [],
    ),
  );

  if (groupPlaceholders.length === 0 || pattern.status === 'invalid') {
    return problems;
  }

  if (pattern.status === 'none') {
    problems.push(
      `${label} uses ${formatQuotedList(groupPlaceholders)}, but no metric name pattern is set. Set a metric name pattern with capture groups or use {metric}.`,
    );
    return problems;
  }

  const outOfRangeGroups = unique(
    parsed.tokens.flatMap((token) =>
      token.kind === 'group-number' && token.groupNumber > pattern.groupCount
        ? [token.placeholder]
        : [],
    ),
  );

  if (outOfRangeGroups.length > 0) {
    problems.push(
      `${label} uses ${formatQuotedList(outOfRangeGroups)}, but the metric name pattern has ${pluralize(pattern.groupCount, '1 capture group', `${pattern.groupCount} capture groups`)}.`,
    );
  }

  const unknownGroupNames = unique(
    parsed.tokens.flatMap((token) =>
      token.kind === 'group-name' && !pattern.groupIndexByName.has(token.groupName)
        ? [token.placeholder]
        : [],
    ),
  );

  if (unknownGroupNames.length > 0) {
    problems.push(
      `${label} has ${pluralize(unknownGroupNames.length, 'an unknown placeholder', 'unknown placeholders')} ${formatQuotedList(unknownGroupNames)}. Available placeholders: ${listPlaceholders(pattern).join(', ')}.`,
    );
  }

  return problems;
}

function listPlaceholders(pattern: Exclude<PatternAnalysis, { status: 'invalid' }>): string[] {
  const placeholders = [`{${METRIC_PLACEHOLDER}}`];

  if (pattern.status === 'none') {
    return placeholders;
  }

  for (let groupNumber = 1; groupNumber <= pattern.groupCount; groupNumber++) {
    placeholders.push(`{${groupNumber}}`);
  }

  for (const groupName of pattern.groupIndexByName.keys()) {
    if (IDENTIFIER_REGEX.test(groupName) && groupName !== METRIC_PLACEHOLDER) {
      placeholders.push(`{${groupName}}`);
    }
  }

  return placeholders;
}

function renderTemplate(
  tokens: TemplateToken[],
  metricName: string,
  groups: readonly string[] | undefined,
  pattern: PatternAnalysis,
): string {
  return tokens
    .map((token) => {
      switch (token.kind) {
        case 'literal':
          return token.text;
        case 'metric':
          return metricName;
        case 'group-number':
          return groups?.[token.groupNumber] ?? '';

        case 'group-name': {
          const groupNumber =
            pattern.status === 'valid' ? pattern.groupIndexByName.get(token.groupName) : undefined;

          return groupNumber === undefined ? '' : groups?.[groupNumber] ?? '';
        }
      }
    })
    .join('');
}

function hasSurroundingWhitespace(value: string): boolean {
  return value.trim() !== value;
}

function describeSurroundingWhitespace(label: string, value: string): string {
  return `${label} "${value}" starts or ends with a space. Remove it.`;
}

function floatSeries(name: string): NonNullable<ErrorBandsConfig['options']['upperBandMetric']> {
  return { name, type: 'floatSeries' };
}

function formatPositions(positions: number[]): string {
  return positions.length === 1 ? `position ${positions[0]}` : `positions ${positions.join(', ')}`;
}

function formatQuotedList(values: string[]): string {
  return values.map((value) => `"${value}"`).join(', ');
}

function pluralize(count: number, singular: string, plural: string): string {
  return count === 1 ? singular : plural;
}

function unique(values: string[]): string[] {
  return [...new Set(values)];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasOwn(record: object, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(record, key);
}

function setCached<T>(cache: Map<string, T>, key: string, value: T) {
  if (cache.size >= CACHE_LIMIT) {
    const oldest = cache.keys().next();

    if (!oldest.done) {
      cache.delete(oldest.value);
    }
  }

  cache.set(key, value);
}
